"use client";
import type { Document } from "@contentful/rich-text-types";
import { ModuleCreator } from "@lib/contentful/modules-renderer/ModuleCreator";
import { getModuleComponent } from "@lib/contentful/modules-renderer/modules-map";
import type { ModuleTypes } from "@lib/contentful/mappers/modules/constants";
import type { PageModule } from "@lib/contentful/mappers/modules/types";
import { getModuleType, withModuleKeys } from "@lib/contentful/mappers/modules/utils";
import { Card } from "@ecco/ui/card";
import type { FC } from "react";

function isRenderableModule(module: PageModule): boolean {
    return Boolean(getModuleComponent(getModuleType(module)));
}

interface IModule {
    index: number;
    module: PageModule;
    authorizationInvitation?: Document;
    theme?: string;
}

export const Module: FC<IModule> = ({ module: moduleData, index, ...rest }) => {
    const type = getModuleType(moduleData);

    return (
        <ModuleCreator
            module={moduleData}
            index={index}
            type={type}
            component={getModuleComponent(type)}
            {...rest}
        />
    );
};

const cardModules: string[] = [
    "moduleCardGeneric",
    "moduleCardLinkCollection",
] satisfies `${ModuleTypes}`[];

const isAlreadyCard = (type: string) => cardModules.includes(type);

interface ModulesRendererProps {
    modules?: PageModule[];
    wrapInsideCard?: boolean;
    authorizationInvitation?: Document;
    theme?: string;
}

export const ModulesRenderer: FC<ModulesRendererProps> = ({
    modules = [],
    wrapInsideCard = false,
    authorizationInvitation,
    theme,
}) => {
    return (
        <>
            {withModuleKeys(modules).map(({ key, module: mod }, i) => {
                if (!isRenderableModule(mod)) {
                    return null;
                }

                const type = getModuleType(mod);

                const moduleData = (
                    <Module
                        module={mod}
                        key={key}
                        index={i}
                        authorizationInvitation={authorizationInvitation}
                        theme={theme}
                    />
                );

                if (!wrapInsideCard) {
                    return moduleData;
                }

                return isAlreadyCard(type) ? moduleData : <Card key={key}>{moduleData}</Card>;
            })}
        </>
    );
};
